import { AlertCircle, Check } from "lucide-react";

/**
 * Sticky error summary shown above the submit button after a failed
 * submission. Lists each form section with a tick (✓) or warning icon,
 * and clicking a section scrolls to it. Stays visible until the user
 * fixes every flagged section , significantly more discoverable than a
 * toast that auto-dismisses.
 *
 * Sections is an array of:
 *   { id: "about", label: "About you", ok: true | false }
 */
export const ValidationSummary = ({ sections, visible }) => {
  if (!visible) return null;
  const failing = sections.filter((s) => !s.ok);
  if (failing.length === 0) return null;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 132;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div
      className="mb-5 rounded-2xl border border-red-200 bg-red-50/50 p-4 md:p-5"
      role="alert"
      data-testid="validation-summary"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600 shrink-0">
          <AlertCircle size={16} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-['Outfit'] font-semibold text-[var(--charcoal)] text-[15px] leading-tight">
            We still need a few details before submitting.
          </p>
          <p className="text-[13px] text-[var(--text-muted)] mt-0.5">
            Tap a section to jump to it.
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => scrollTo(s.id)}
                  className={`w-full text-left inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                    s.ok
                      ? "text-[var(--text-muted)] cursor-default"
                      : "bg-white text-red-700 border border-red-200 hover:bg-red-100"
                  }`}
                  disabled={s.ok}
                  data-testid={`validation-summary-${s.id}`}
                >
                  {s.ok ? (
                    <Check
                      size={14}
                      strokeWidth={3}
                      className="text-emerald-600 shrink-0"
                    />
                  ) : (
                    <AlertCircle size={14} className="text-red-500 shrink-0" />
                  )}
                  <span className="truncate">{s.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ValidationSummary;
